import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Button,
  Input,
  FormControl,
  Select,
  Option,
  Box,
  Typography,
  Stack
} from '@mui/joy';
import { Tag, CreateTagData, UpdateTagData } from '../types/tag';

interface TagFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTagData | UpdateTagData) => Promise<void>;
  tag?: Tag;
  title?: string;
  loading?: boolean;
}

const PRESET_COLORS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
  '#F97316', '#06B6D4', '#EC4899', '#6366F1', '#6B7280',
  '#DC2626', '#D97706', '#059669', '#2563EB', '#7C3AED',
  '#EA580C', '#0891B2', '#DB2777', '#4F46E5', '#374151'
];

const PRESET_CATEGORIES = [
  'priority', 'requirements', 'compensation', 'coverage',
  'events', 'location', 'availability', 'special'
];

export const TagForm: React.FC<TagFormProps> = ({
  open,
  onClose,
  onSubmit,
  tag,
  title = tag ? 'Edit Tag' : 'Create Tag',
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateTagData>({
    name: '',
    description: '',
    color: '#6B7280',
    icon: '',
    category: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (tag) {
      setFormData({
        name: tag.name,
        description: tag.description || '',
        color: tag.color,
        icon: tag.icon || '',
        category: tag.category || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#6B7280',
        icon: '',
        category: '',
      });
    }
    setErrors({});
  }, [tag, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tag name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Tag name must be 100 characters or less';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (formData.category && formData.category.length > 50) {
      newErrors.category = 'Category must be 50 characters or less';
    }

    if (formData.icon && formData.icon.length > 50) {
      newErrors.icon = 'Icon must be 50 characters or less';
    }

    // Validate color format
    if (!/^#[0-9A-F]{6}$/i.test(formData.color || '')) {
      newErrors.color = 'Invalid color format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting tag:', error);
    }
  };

  const handleInputChange = (field: keyof CreateTagData, value: string | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md">
        <ModalClose />
        <Typography level="h4" component="h2" mb={2}>
          {title}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <FormControl error={!!errors.name}>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Tag Name *
              </Typography>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter tag name"
                required
              />
              {errors.name && (
                <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                  {errors.name}
                </Typography>
              )}
            </FormControl>

            <FormControl error={!!errors.description}>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Description
              </Typography>
              <Input
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value || '')}
                placeholder="Enter tag description"
              />
              {errors.description && (
                <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                  {errors.description}
                </Typography>
              )}
            </FormControl>

            <FormControl error={!!errors.color}>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Color *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Input
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  placeholder="#RRGGBB"
                  sx={{ flex: 1 }}
                />
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: formData.color,
                    border: '2px solid',
                    borderColor: 'divider',
                  }}
                />
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography level="body-sm" sx={{ mb: 1 }}>
                  Preset Colors:
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {PRESET_COLORS.map((color) => (
                    <Box
                      key={color}
                      onClick={() => handleInputChange('color', color)}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 0.5,
                        backgroundColor: color,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: formData.color === color ? 'primary.500' : 'transparent',
                        '&:hover': {
                          borderColor: 'primary.300',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
              {errors.color && (
                <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                  {errors.color}
                </Typography>
              )}
            </FormControl>

            <FormControl error={!!errors.category}>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Category
              </Typography>
              <Select
                value={formData.category}
                onChange={(_, value) => handleInputChange('category', value || '')}
                placeholder="Select category"
              >
                <Option value="">No Category</Option>
                {PRESET_CATEGORIES.map((cat) => (
                  <Option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </Option>
                ))}
              </Select>
              {errors.category && (
                <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                  {errors.category}
                </Typography>
              )}
            </FormControl>

            <FormControl error={!!errors.icon}>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Icon
              </Typography>
              <Input
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                placeholder="Enter icon name (optional)"
              />
              {errors.icon && (
                <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                  {errors.icon}
                </Typography>
              )}
            </FormControl>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                {tag ? 'Update' : 'Create'} Tag
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}; 